import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Use this for ALL user-generated or AI-generated HTML content
 * before rendering with dangerouslySetInnerHTML.
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';
  
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'p', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'br', 'hr',
      'a', 'span', 'div',
      'img', 'figure', 'figcaption',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
      'blockquote', 'code', 'pre',
      'mark', 'sub', 'sup',
    ],
    ALLOWED_ATTR: [
      'class', 'id',
      'href', 'target', 'rel',
      'src', 'alt', 'loading', 'width', 'height',
      'colspan', 'rowspan',
      'style', // For inline-styled pricing tables (border, padding, background-color only)
      'data-refinement', // For AI refinement highlighting
    ],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    FORCE_BODY: true,
    // Force all links to open in new tab with noopener
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  });
}

/**
 * Sanitize HTML and ensure links are safe.
 * Adds rel="noopener noreferrer" to all anchor tags.
 */
export function sanitizeHtmlWithSafeLinks(dirty: string | null | undefined): string {
  const sanitized = sanitizeHtml(dirty);
  
  // Add noopener noreferrer to all links
  return sanitized.replace(
    /<a\s+([^>]*href=[^>]*)>/gi,
    (match, attrs) => {
      if (attrs.includes('rel=')) {
        return match;
      }
      return `<a ${attrs} rel="noopener noreferrer">`;
    }
  );
}
